/**
 * @fileoverview "Shared with me": private pages and sites other people have
 * shared with the signed-in reader.
 *
 * Each row says who shared something and when, which is all the relay knows.
 * What it is stays sealed until the reader opens it, because that costs their
 * signer two decryptions and a remote signer may ask them to approve each one.
 * `sharedWithMe.ts` carries the reasoning.
 */

import type { SignerInterface } from '@cloistr/auth/core';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { RequireSigner } from '@/components/RequireSigner';
import { PAGE_COLLECTION_KIND } from '@/services/pages/kinds';
import {
  listShared,
  openShared,
  senderNames,
  shortNpub,
  type OpenedShare,
  type SharedRow,
} from '@/services/pages/sharedWithMe';

export function SharedRoute() {
  return (
    <RequireSigner
      title="Shared with me"
      callToAction="Sign in to see the private pages people have shared with you."
    >
      {(signer, pubkey) => <Shared signer={signer} pubkey={pubkey} />}
    </RequireSigner>
  );
}

function Shared({ signer, pubkey }: { signer: SignerInterface; pubkey: string }) {
  const [rows, setRows] = useState<SharedRow[] | null>(null);
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const [opened, setOpened] = useState<Map<string, OpenedShare | 'opening'>>(new Map());
  const [openingAll, setOpeningAll] = useState(false);

  // KEYED ON THE PUBKEY, never on the signer object: once a session is
  // restored the auth package mints a new signer on every render, and an
  // effect depending on it would query the relays forever.
  const signerRef = useRef(signer);
  signerRef.current = signer;

  useEffect(() => {
    let live = true;
    void listShared(pubkey).then((loaded) => {
      if (!live) return;
      setRows(loaded);
      void senderNames(loaded.map((r) => r.from)).then((n) => {
        if (live) setNames(n);
      });
    });
    return () => {
      live = false;
    };
  }, [pubkey]);

  if (rows === null) return <p className="editor-note">Looking for what has been shared with you…</p>;

  async function open(row: SharedRow) {
    setOpened((m) => new Map(m).set(row.id, 'opening'));
    const result = await openShared(row, signerRef.current);
    setOpened((m) => new Map(m).set(row.id, result));
  }

  async function openAll() {
    setOpeningAll(true);
    try {
      // One at a time, so a remote signer shows one prompt at a time.
      for (const row of rows ?? []) {
        if (!opened.has(row.id)) await open(row);
      }
    } finally {
      setOpeningAll(false);
    }
  }

  const unopened = rows.filter((r) => !opened.has(r.id)).length;

  return (
    <section className="editor">
      <h1 className="editor-heading">Shared with me</h1>
      <p className="editor-note">
        Private pages and sites people have shared with you. Your own are in{' '}
        <Link to="/mine">Your pages</Link>.
      </p>

      {rows.length === 0 ? (
        <p className="editor-note">Nothing has been shared with you yet.</p>
      ) : (
        <>
          {unopened > 0 && (
            <p className="editor-note">
              What each share holds is sealed until you open it. Your signer decrypts each one and may ask you
              to approve it.{' '}
              <button type="button" onClick={() => void openAll()} disabled={openingAll}>
                {openingAll ? 'Opening…' : 'Open all'}
              </button>
            </p>
          )}
          <ul className="share-list">
            {rows.map((row) => (
              <Row
                key={row.id}
                row={row}
                sender={names.get(row.from) ?? shortNpub(row.from)}
                state={opened.get(row.id)}
                onOpen={() => void open(row)}
              />
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function Row({
  row,
  sender,
  state,
  onOpen,
}: {
  row: SharedRow;
  sender: string;
  state: OpenedShare | 'opening' | undefined;
  onOpen: () => void;
}) {
  const when = new Date(row.sharedAt * 1000).toLocaleString();
  const from = (
    <span className="editor-label">
      from {sender} · {when}
    </span>
  );

  if (state === undefined || state === 'opening') {
    return (
      <li>
        <strong>Sealed share</strong> {from}{' '}
        <button type="button" onClick={onOpen} disabled={state === 'opening'}>
          {state === 'opening' ? 'Opening…' : 'Open'}
        </button>
      </li>
    );
  }
  if (state.status === 'unreadable') {
    return (
      <li>
        <strong>A share this app cannot read</strong> {from}
      </li>
    );
  }
  const kindName = state.kind === PAGE_COLLECTION_KIND ? 'Site' : 'Page';
  if (state.status === 'removed') {
    return (
      <li>
        <strong>Removed</strong> <span className="editor-label">{kindName}</span> {from}
      </li>
    );
  }
  return (
    <li>
      <strong>{state.title ?? 'Untitled'}</strong> <span className="editor-label">{kindName}</span> {from}{' '}
      <Link to={`/${state.naddr}`}>Open</Link>
    </li>
  );
}
